import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Slide,
  Dialog,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WorkspaceIcon from "@mui/icons-material/Workspaces";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import { keyframes } from "@mui/material/styles";
import { useAppDispatch, useAppSelector } from "src/store/app/hooks";
import { createLibraryAPI } from "src/store/features/library/createLibrarySlice";
import { listLibraryAPI } from "src/store/features/library/libraryListSlice";
import { existWorkSpaceCheckAPI } from "src/store/features/ppi/ppi_file_upload/existFileCheckSlice";
import {
  setLibrary,
  setUserInputWorkSpaceName,
} from "src/store/features/ppi/ppi-user-parameters/ppiUserParametersSlice";
import { useTranslation } from "react-i18next";

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { opacity: 0.7; transform: scale(0.95); }
  50% { opacity: 1; transform: scale(1.05); }
  100% { opacity: 0.7; transform: scale(0.95); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const MODAL_ANIMATIONS = `
  @keyframes modalIn {
    from { opacity: 0; transform: translateY(12px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes iconPop {
    0%   { transform: scale(0.6); opacity: 0; }
    70%  { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes fadeSlide {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const BACKDROP_PROPS = {
  sx: { backgroundColor: "rgba(0,0,0,0.35)", backdropFilter: "blur(6px)" },
};

const ModalDivider = () => (
  <Box sx={{ px: 3.5 }}>
    <Box sx={{ height: "1px", background: "#f1f5f9" }} />
  </Box>
);

interface MakeWorkSpaceProps {
  onNextStep?: (result: any) => void;
  isOpen?: boolean;
}

const MakeWorkSpace: React.FC<MakeWorkSpaceProps> = ({
  onNextStep,
  isOpen = true,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // 모달 상태
  const [open, setOpen] = useState(isOpen);
  const [hasClosedOnce, setHasClosedOnce] = useState(false);
  const [userLibraryName, setUserLibraryName] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalType, setModalType] = useState<"main" | "loading" | "duplicate" | "success">("main");

  // 알림 상태
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });

  // 선택 정보
  const userChoiceToolName = useAppSelector((state) => state?.userParameter?.userChoiceTool);
  const userHaveAssayFile = useAppSelector((state) => state?.userParameter?.userHaveAssayFile);
  const userChoiceSelectTool = useAppSelector((state) => state?.userParameter?.userChoiceTool);
  const userChoiceDrugResponseSelectToolType = useAppSelector((state)=> state?.userParameter?.userChoiceToolType);

  // 열림 동기화
  useEffect(() => {
    if (!hasClosedOnce && isOpen) setOpen(true);
  }, [isOpen, hasClosedOnce]);

  // 입력
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filtered = e.target.value.replace(/[^a-zA-Z0-9가-힣_.:=+\-@]/g, "");
    setUserLibraryName(filtered);
    dispatch(setUserInputWorkSpaceName(filtered));
  };

  // 알림 닫기
  const handleCloseNotification = () =>
    setNotification({ ...notification, open: false });

  // 닫기
  const handleClose = () => {
    if (modalType !== "loading" && !isProcessing) {
      setOpen(false);
      setHasClosedOnce(true);
      if (onNextStep)
        onNextStep({ closed: true, createdAt: new Date().toISOString() });
    }
  };

  // 생성
  const handleCreateLibrary = async () => {
    // 중복 클릭 방지
    if (isProcessing) return;

    // 이름 확인
    if (!userLibraryName.trim()) {
      setNotification({
        open: true,
        message:
          t(`ppi_parameter.enterWorkSpaceName`) ||
          "워크스페이스 이름을 입력해주세요.",
        severity: "error",
      });
      return;
    }
    // 도구 확인
    if (!userChoiceToolName) {
      setNotification({
        open: true,
        message: "도구 타입이 선택되지 않았습니다.",
        severity: "error",
      });
      return;
    }
    // 생성 요청
    try {
      // 로딩 전환
      setIsProcessing(true);
      setModalType("loading");

      // 중복 확인 요청
      const duplicateCheckResult = await dispatch(
        existWorkSpaceCheckAPI({
          group_name: "temp_group",
          library_name: userLibraryName,
        })
      ).unwrap();
      // 중복 확인
      if (duplicateCheckResult.exists === true) {
        setModalType("duplicate");
        setIsProcessing(false);
        return;
      }

      // 타입이 있는 워크플로우 생성
      if(userChoiceSelectTool === 'Drug Response' || userChoiceSelectTool === 'Drug Toxicity' || userChoiceSelectTool === 'MDTR'){
        await dispatch(
          createLibraryAPI({
            group_name: "temp_group",
            library_name: userLibraryName,
            workflow_name: userChoiceSelectTool === "Drug Toxicity" ? "drug_toxicity" : userChoiceToolName,
            assay_use: userHaveAssayFile,
            workflow_type : userChoiceSelectTool === "Drug Toxicity" && userChoiceDrugResponseSelectToolType === "mdtr" ? "mdtr" : userChoiceDrugResponseSelectToolType
          })
        ).unwrap();
      }else{
        // 일반 생성
        await dispatch(
          createLibraryAPI({
            group_name: "temp_group",
            library_name: userLibraryName,
            workflow_name: userChoiceSelectTool === "Drug Toxicity" ? "drug_toxicity" : userChoiceToolName,
            assay_use: userHaveAssayFile
          })
        ).unwrap();
      }

      // 목록 갱신
      await dispatch(listLibraryAPI({ group_name: "temp_group" })).unwrap();
      if (userLibraryName) dispatch(setLibrary(userLibraryName));
      // 완료
      setModalType("success");
      setTimeout(() => {
        // 생성 결과 전달
        if (onNextStep)
          onNextStep({
            name: userLibraryName,
            createdAt: new Date().toISOString(),
            success: true,
            selectedTool: userChoiceToolName,
            workflow_type: userChoiceSelectTool === "Drug Toxicity" && userChoiceDrugResponseSelectToolType === "mdtr" ? "mdtr" : userChoiceDrugResponseSelectToolType,
          });

        // 종료 처리
        setOpen(false);
        setIsProcessing(false);

        // 성공 알림
        setNotification({
          open: true,
          message: t(`PPI.modal.creationSuccess`),
          severity: "success",
        });
      }, 2000);
    } catch (error) {
      // 실패
      console.error("워크스페이스 생성 실패:", error);
      setIsProcessing(false);
      setModalType("main");
      setNotification({
        open: true,
        message: t(`PPI.modal.errorMessage`),
        severity: "error",
      });
    }
  };

  // 재입력
  const handleRetryWithNewName = () => {
    // 초기화
    setModalType("main");
    setUserLibraryName("");
    dispatch(setUserInputWorkSpaceName(""));
    setIsProcessing(false);
  };

  // Enter 생성
  const handleWorkspaceNameKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    // 조합 입력 무시
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    event.preventDefault();

    // 생성 가능 여부
    if (!userLibraryName.trim() || isProcessing) return;
    handleCreateLibrary();
  };

  // 도구명
  const getToolDisplayName = () => {
    if (userChoiceToolName === "Drug Toxicity") {
      if (userChoiceDrugResponseSelectToolType === "fatetox") return "FateTox";
      if (userChoiceDrugResponseSelectToolType === "mdtr") return "MDTR";
      if (userChoiceDrugResponseSelectToolType === "animal") return "In Vivo Acute Toxicity";
    }
    if (userChoiceToolName === "MDTR") return "MDTR";

    switch (userChoiceToolName) {
      case "ppi_inhibitor":
        return "PPI Inhibitor";
      case "kinase_inhibitor":
        return "Kinase Inhibitor";
      case "drug_response":
        return "Drug Response";
      default:
        return userChoiceToolName || "Unknown";
    }
  };

  return (
    <>
      <style>{MODAL_ANIMATIONS}</style>

      <Dialog
        disableScrollLock={false}
        disableAutoFocus={true}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              "0 24px 48px -8px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.05)",
            background: "#ffffff",
            minWidth: "480px",
            maxWidth: "540px",
            animation: "modalIn 0.3s cubic-bezier(0.16,1,0.3,1) both",
          },
        }}
        BackdropProps={BACKDROP_PROPS}
      >
        {/* 입력 */}
        {modalType === "main" && (
          <>
            {/* 입력 헤더 */}
            <Box
              sx={{
                px: 3.5,
                pt: 3.5,
                pb: 2.5,
                animation: "fadeSlide 0.35s ease 0.05s both",
              }}
            >
              {/* 입력 아이콘 */}
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: "#eff6ff",
                  border: "1.5px solid #bfdbfe",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.5,
                  animation:
                    "iconPop 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.1s both",
                }}
              >
                <WorkspaceIcon sx={{ color: "#3b82f6", fontSize: "20px" }} />
              </Box>
              {/* 입력 제목 */}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: "#0f172a",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.3,
                  mb: 0.5,
                }}
              >
                Create WorkSpace
              </Typography>
              <Typography
                sx={{ fontSize: "0.82rem", color: "#64748b", lineHeight: 1.55 }}
              >
                {getToolDisplayName()} {t(`PPI.modal.workspaceCreation`)}
              </Typography>
            </Box>

            <ModalDivider />

            {/* 입력 본문 */}
            <DialogContent
              sx={{
                px: 3.5,
                py: 2.5,
                animation: "fadeSlide 0.35s ease 0.15s both",
              }}
            >
              {/* 도구 정보 */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: "10px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  mb: 2.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                {/* 도구 아이콘 */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    background: "#4968A2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AutoAwesomeIcon sx={{ color: "white", fontSize: "16px" }} />
                </Box>
                {/* 도구명 표시 */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "#94a3b8",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Selected Tool
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.88rem",
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    {getToolDisplayName()}
                  </Typography>
                </Box>
                {/* Assay 표시 */}
                {userChoiceToolName !== "Drug Response" && (
                  <Chip
                    label={
                      userHaveAssayFile
                        ? "With Assay File"
                        : "Without Assay File"
                    }
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      bgcolor: "#dcfce7",
                      color: "#166534",
                    }}
                  />
                )}
              </Box>

              {/* 이름 입력 */}
              <TextField
                fullWidth
                label="Work Space Name"
                variant="outlined"
                value={userLibraryName}
                onChange={handleNameChange}
                onKeyDown={handleWorkspaceNameKeyDown}
                required
                placeholder="Enter workspace name"
                disabled={isProcessing}
                helperText={
                  <Typography
                    component="span"
                    sx={{
                      display: "block",
                      color: "#dc2626",
                      fontSize: "0.72rem",
                      mt: 0.5,
                      fontWeight: 500,
                    }}
                  >
                    * Allowed: English, numbers, Korean, _ . : = + - @
                  </Typography>
                }
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  // 이름 도움말
                  endAdornment: (
                    <Tooltip title={t(`PPI.modal.workspaceName`)}>
                      <IconButton size="small">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: "#f9fafb",
                    fontSize: "0.88rem",
                    "&:hover": { backgroundColor: "#f3f4f6" },
                    "&.Mui-focused": {
                      backgroundColor: "#ffffff",
                      boxShadow: "0 0 0 3px rgba(59,130,246,0.1)",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#6b7280",
                    fontWeight: 500,
                    fontSize: "0.88rem",
                  },
                }}
              />

              {/* 안내 */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  backgroundColor: "#f0f9ff",
                  p: 2,
                  borderRadius: "10px",
                  border: "1px solid #e0f2fe",
                }}
              >
                {/* 안내 아이콘 */}
                <InfoOutlinedIcon
                  sx={{
                    color: "#0284c7",
                    mt: 0.2,
                    fontSize: 18,
                    flexShrink: 0,
                  }}
                />
                {/* 안내 문구 */}
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#0c4a6e",
                      fontSize: "0.82rem",
                      mb: 0.5,
                    }}
                  >
                    {t(`PPI.modal.autoProgress`)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "#64748b",
                      lineHeight: 1.5,
                    }}
                  >
                    {t(`PPI.modal.nextStepNotice`)}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>

            <ModalDivider />

            {/* 입력 액션 */}
            <DialogActions
              sx={{
                px: 3.5,
                py: 2.5,
                gap: 1.5,
                animation: "fadeSlide 0.35s ease 0.25s both",
              }}
            >
              {/* 취소 */}
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isProcessing}
                sx={{
                  borderRadius: "10px",
                  borderColor: "#e2e8f0",
                  color: "#64748b",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  py: 1.2,
                  px: 2.5,
                  textTransform: "none",
                  "&:hover": { borderColor: "#cbd5e1", background: "#f8fafc" },
                }}
              >
                {t(`PPI.cancel`) || "취소"}
              </Button>
              {/* 생성 버튼 */}
              <Button
                variant="contained"
                onClick={handleCreateLibrary}
                disabled={!userLibraryName.trim() || isProcessing}
                sx={{
                  borderRadius: "10px",
                  background: "#0f172a",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  py: 1.2,
                  px: 2.5,
                  textTransform: "none",
                  boxShadow: "0 2px 8px rgba(15,23,42,0.25)",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    background: "#1e293b",
                    boxShadow: "0 4px 14px rgba(15,23,42,0.35)",
                    transform: "translateY(-1px)",
                  },
                  "&:disabled": { background: "#e5e7eb", color: "#9ca3af" },
                }}
              >
                {isProcessing ? "Loading..." : t(`PPI.modal.workspaceCreation`)}
              </Button>
            </DialogActions>
          </>
        )}

        {/* 생성 중 */}
        {modalType === "loading" && (
          <>
            {/* 생성 헤더 */}
            <Box sx={{ px: 3.5, pt: 3.5, pb: 2.5 }}>
              {/* 생성 아이콘 */}
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: "#eff6ff",
                  border: "1.5px solid #bfdbfe",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.5,
                  animation: `${spin} 2s linear infinite`,
                }}
              >
                <AutoAwesomeIcon sx={{ color: "#3b82f6", fontSize: "20px" }} />
              </Box>
              {/* 생성 제목 */}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: "#0f172a",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.3,
                  mb: 0.5,
                }}
              >
                Creating WorkSpace
              </Typography>
              <Typography
                sx={{ fontSize: "0.82rem", color: "#64748b", lineHeight: 1.55 }}
              >
                {t(`PPI.modal.pleaseWait`) || "잠시만 기다려주세요."}
              </Typography>
            </Box>

            <ModalDivider />

            {/* 생성 본문 */}
            <DialogContent sx={{ px: 3.5, py: 2.5, textAlign: "center" }}>
              {/* 생성 심볼 */}
              <Box
                sx={{ animation: `${float} 3s ease-in-out infinite`, mb: 3 }}
              >
                <WorkspaceIcon
                  sx={{
                    fontSize: 72,
                    color: "#4968A2",
                    filter: "drop-shadow(0 4px 8px rgba(99,142,241,0.3))",
                  }}
                />
              </Box>

              {/* 생성 진행률 */}
              <LinearProgress
                sx={{
                  height: 6,
                  borderRadius: 3,
                  mb: 2.5,
                  bgcolor: "rgba(59,130,246,0.1)",
                  "& .MuiLinearProgress-bar": {
                    background:
                      "linear-gradient(90deg, #4968A2 0%, #3b82f6 100%)",
                  },
                }}
              />

              {/* 생성 상태 */}
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "#0f172a",
                  fontSize: "0.92rem",
                  mb: 0.5,
                }}
              >
                {userLibraryName} {t(`PPI.modal.workspaceCreating`)}
              </Typography>
              <Typography
                sx={{ fontSize: "0.8rem", color: "#64748b", mb: 2.5 }}
              >
                {t(`PPI.modal.creatingLibraryWait`)}
              </Typography>

              {/* 생성 단계 */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  p: 2.5,
                  backgroundColor: "#f8fafc",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                {[
                  { label: t(`PPI.modal.checkingDuplicateName`), active: true },
                  { label: t(`PPI.modal.workspaceConfig`), active: false },
                  { label: t(`PPI.modal.setupInProgress`), active: false },
                ].map((step, i) => (
                  // 단계 항목
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      opacity: step.active ? 1 : 0.4,
                    }}
                  >
                    {step.active ? (
                      <CircularProgress size={14} sx={{ color: "#3b82f6" }} />
                    ) : (
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          border: "1.5px solid #cbd5e1",
                        }}
                      />
                    )}
                    <Typography sx={{ fontSize: "0.8rem", color: "#475569" }}>
                      {step.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </DialogContent>
          </>
        )}

        {/* 중복 */}
        {modalType === "duplicate" && (
          <>
            {/* 중복 헤더 */}
            <Box sx={{ px: 3.5, pt: 3.5, pb: 2.5 }}>
              {/* 중복 아이콘 */}
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: "#fef2f2",
                  border: "1.5px solid #fecaca",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.5,
                  animation:
                    "iconPop 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.1s both",
                }}
              >
                <ErrorOutlineIcon sx={{ color: "#ef4444", fontSize: "20px" }} />
              </Box>
              {/* 중복 제목 */}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: "#0f172a",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.3,
                  mb: 0.5,
                }}
              >
                {t(`PPI.modal.workspaceDuplicate`)}
              </Typography>
              <Typography
                sx={{ fontSize: "0.82rem", color: "#64748b", lineHeight: 1.55 }}
              >
                {t(`PPI.modal.chooseAnotherWorkspace`)}
              </Typography>
            </Box>

            <ModalDivider />

            {/* 중복 본문 */}
            <DialogContent sx={{ px: 3.5, py: 2.5, textAlign: "center" }}>
              {/* 중복 심볼 */}
              <Box
                sx={{ animation: `${pulse} 2s ease-in-out infinite`, mb: 2.5 }}
              >
                <ErrorOutlineIcon
                  sx={{
                    fontSize: 72,
                    color: "#ef4444",
                    filter: "drop-shadow(0 4px 8px rgba(239,68,68,0.3))",
                  }}
                />
              </Box>

              {/* 중복 이름 */}
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "#0f172a",
                  fontSize: "0.92rem",
                  mb: 2,
                }}
              >
                {userLibraryName} {t(`PPI.modal.workspaceInUse`)}
              </Typography>

              {/* 해결 안내 */}
              <Box
                sx={{
                  backgroundColor: "#fef2f2",
                  borderRadius: "10px",
                  padding: "16px 20px",
                  border: "1px solid #fecaca",
                  textAlign: "left",
                }}
              >
                <Typography
                  sx={{
                    color: "#7f1d1d",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    mb: 0.5,
                  }}
                >
                  💡 {t(`PPI.modal.solution`)}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.8rem", color: "#991b1b", lineHeight: 1.5 }}
                >
                  {t(`PPI.modal.nameUnique`)}
                </Typography>
              </Box>
            </DialogContent>

            <ModalDivider />

            {/* 중복 액션 */}
            <DialogActions sx={{ px: 3.5, py: 2.5 }}>
              {/* 재입력 버튼 */}
              <Button
                variant="contained"
                onClick={handleRetryWithNewName}
                startIcon={<RefreshIcon sx={{ fontSize: "16px !important" }} />}
                fullWidth
                sx={{
                  borderRadius: "10px",
                  background:
                    "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  py: 1.2,
                  textTransform: "none",
                  boxShadow: "0 2px 8px rgba(239,68,68,0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 8px 16px -4px rgba(239,68,68,0.3)",
                  },
                }}
              >
                {t(`PPI.modal.retryNewName`)}
              </Button>
            </DialogActions>
          </>
        )}

        {/* 완료 */}
        {modalType === "success" && (
          <>
            {/* 완료 헤더 */}
            <Box sx={{ px: 3.5, pt: 3.5, pb: 2.5 }}>
              {/* 완료 아이콘 */}
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: "#ecfdf5",
                  border: "1.5px solid #a7f3d0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.5,
                  animation: `${pulse} 1.5s ease-in-out infinite`,
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ color: "#10b981", fontSize: "22px" }}
                />
              </Box>
              {/* 완료 제목 */}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: "#0f172a",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.3,
                  mb: 0.5,
                }}
              >
                {t(`PPI.modal.creationComplete`)}
              </Typography>
              <Typography
                sx={{ fontSize: "0.82rem", color: "#64748b", lineHeight: 1.55 }}
              >
                {t(`PPI.modal.workspaceReady`)}
              </Typography>
            </Box>

            <ModalDivider />

            {/* 완료 본문 */}
            <DialogContent sx={{ px: 3.5, py: 2.5, textAlign: "center" }}>
              {/* 완료 심볼 */}
              <Box
                sx={{ animation: `${float} 3s ease-in-out infinite`, mb: 2.5 }}
              >
                <CheckCircleOutlineIcon
                  sx={{
                    fontSize: 80,
                    color: "#10b981",
                    filter: "drop-shadow(0 4px 8px rgba(16,185,129,0.3))",
                  }}
                />
              </Box>

              {/* 완료 메시지 */}
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#10b981",
                  fontSize: "1.4rem",
                  mb: 0.5,
                }}
              >
                {t(`PPI.modal.success2`)}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "#0f172a",
                  fontSize: "0.92rem",
                  mb: 2.5,
                }}
              >
                {userLibraryName} {t(`PPI.modal.success`)}
              </Typography>

              {/* 다음 단계 안내 */}
              <Box
                sx={{
                  backgroundColor: "#ecfdf5",
                  borderRadius: "10px",
                  padding: "16px 20px",
                  border: "1px solid #bbf7d0",
                  textAlign: "left",
                }}
              >
                <Typography
                  sx={{
                    color: "#065f46",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    mb: 0.5,
                  }}
                >
                  {t(`PPI.modal.nextStep`)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "#6b7280",
                    lineHeight: 1.5,
                    mb: 1.5,
                  }}
                >
                  {t(`PPI.modal.redirecting`)}
                </Typography>
                {/* 이동 진행률 */}
                <LinearProgress
                  sx={{
                    height: 5,
                    borderRadius: 3,
                    bgcolor: "rgba(16,185,129,0.1)",
                    "& .MuiLinearProgress-bar": {
                      background:
                        "linear-gradient(90deg, #10b981 0%, #059669 100%)",
                    },
                  }}
                />
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        TransitionComponent={Slide}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px", fontWeight: 500 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MakeWorkSpace;
